import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useRecordContext, useTranslate } from "react-admin";
import { Link as RouterLink } from "react-router-dom";

import { PractitionerStatus } from "../../generated/graphql";
import { useLearningProgress } from "../../hooks/useLearningProgress";
import { color } from "../../theme/designTokens";
import type { PractitionerRecord } from "../PractitionerShow";
import { LearningProgressSummaryTiles, LearningProgressTable } from "./LearningProgressTable";

const LearningProgressSummaryForUser = ({ userId }: { userId: string }) => {
  const translate = useTranslate();
  const { caregivers, courses, entries, loading, error, reload } = useLearningProgress({ userId });

  if (loading) return <CircularProgress aria-label={translate("learningProgress.loading")} />;
  if (error) {
    return (
      <Alert
        severity="error"
        action={<Button onClick={() => void reload()}>{translate("learningProgress.actions.retry")}</Button>}
      >
        {translate("learningProgress.loadError")}
      </Alert>
    );
  }

  return (
    <Stack gap={1.5} data-testid="practitioner-learning-progress">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography component="h2" variant="h6">
          {translate("learningProgress.practitionerTitle")}
        </Typography>
        <Button component={RouterLink} to={`/LearningProgress?caregiver=${encodeURIComponent(userId)}`}>
          {translate("learningProgress.actions.openReport")}
        </Button>
      </Box>
      {entries.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center", borderColor: color.panelBorder }}>
          <Typography color="text.secondary">{translate("learningProgress.practitionerEmpty")}</Typography>
        </Paper>
      ) : (
        <>
          <LearningProgressSummaryTiles entries={entries} />
          <Paper variant="outlined" sx={{ borderColor: color.panelBorder }}>
            <LearningProgressTable entries={entries} caregivers={caregivers} courses={courses} primaryColumn="course" />
          </Paper>
        </>
      )}
    </Stack>
  );
};

export const LearningProgressSummary = () => {
  const record = useRecordContext<PractitionerRecord>();
  if (!record?.userId || record.status === PractitionerStatus.Inactive) return null;
  return <LearningProgressSummaryForUser userId={record.userId} />;
};
